import { AngularComponentsModule } from '@abgov/angular-components';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withComponentInputBinding,
  withRouterConfig,
} from '@angular/router';
import { InMemoryCache } from '@apollo/client/core';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { authInterceptor } from '@libs/security/interceptors/auth-interceptor';
import { KeycloakService } from '@libs/security/services/keycloak/keycloak.service';
import { ConfigInitService } from '@libs/services/config-init.service';
import { Apollo, provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { ClassValidatorFormBuilderModule } from 'ngx-reactive-form-class-validator';
import { appRoutes } from './app.routes';
import { DataEntryStateService } from './services/data-entry-state.service';

export function appConfig(): ApplicationConfig {
  return {
    providers: [
      // Enable zoneless mode
      provideZonelessChangeDetection(),

      // Provide Keycloak service
      KeycloakService,

      // Load configuration first
      provideAppInitializer(() => {
        const configService = inject(ConfigInitService);
        const dataEntryStateService = inject(DataEntryStateService);
        const keycloakService = inject(KeycloakService);

        return (async () => {
          await configService.loadConfig();
          const config = configService.getSecuredConfig();
          if (!config.ENVIRONMENT_NAME?.includes('prod')) {
            loadDevMessages();
            loadErrorMessages();
          }
          dataEntryStateService.loadConfig(config.TIMEOUT_DELAY);

          await keycloakService
            .init(
              {
                url: config.KEYCLOAK_URL,
                realm: config.KEYCLOAK_REALM,
                clientId: config.KEYCLOAK_CLIENTID,
              },
              {
                onLoad: 'check-sso',
                checkLoginIframe: !config.KEYCLOAK_DISABLE_IFRAME,
                silentCheckSsoRedirectUri:
                  window.location.origin + '/assets/silent-check-sso.html',
                enableLogging: config.ENVIRONMENT_NAME?.includes('dev'),
              }
            )
            .toPromise();
        })();
      }),

      // Provide Apollo with dynamic config
      provideApollo(() => {
        const httpLink = inject(HttpLink);
        const configService = inject(ConfigInitService);
        const config = configService.getSecuredConfig();

        const http = httpLink.create({
          uri: config.APOLLO_API_URL,
        });

        return {
          link: http,
          cache: new InMemoryCache(),
          connectToDevTools: config.ENVIRONMENT_NAME?.includes('dev'),
        };
      }),

      provideRouter(
        appRoutes,
        withComponentInputBinding(),
        withRouterConfig({
          paramsInheritanceStrategy: 'always',
        })
      ),

      provideHttpClient(withInterceptors([authInterceptor])),

      // enhanced form validator extended from class-validator
      importProvidersFrom(
        Apollo,
        AngularComponentsModule,
        FormsModule,
        ReactiveFormsModule,
        ClassValidatorFormBuilderModule.forRoot()
      ),

      provideAnimations(),
    ],
  };
}
